import type { DocumentExtraction } from './types';

/**
 * A flattened field row in the preview summary. Path is a dotted/bracketed key trail
 * (e.g. `documentLines[2].amount`); label is a human-friendly version of the same trail.
 * `was` is set whenever the proposed value differs from the baseline value.
 */
export interface SummaryRow {
  field: string;
  label: string;
  value: string;
  was?: string;
}

export interface LineCell {
  field: string;
  label: string;
  value: string;
  was?: string;
}

export interface LineRow {
  index: number;
  cells: LineCell[];
  /** True when this row exists on only one side (added or removed in the proposed extraction). */
  ghost: boolean;
}

export interface LinesDiff {
  /** Union of every column header that appears in either side, in stable order. */
  columns: { field: string; label: string }[];
  before: LineRow[];
  after: LineRow[];
  changed: boolean;
}

/** A named `LinesDiff` for any array-of-objects encountered outside the main line-items field. */
export interface ObjectArrayDiff extends LinesDiff {
  /** Dotted/bracketed path to the array in the document tree. */
  field: string;
  /** Humanized path used as the section heading. */
  label: string;
  /** Counts of non-ghost rows on either side, for headline summaries. */
  rowCount: number;
  rowCountWas: number;
}

export interface PreviewSummary {
  /** Scalar/object field rows, every leaf field present on either side, with `was` when changed. */
  scalarRows: SummaryRow[];
  /** Subset of `scalarRows` that actually changed — for the always-visible Changes list. */
  scalarChanges: SummaryRow[];
  /** Line-item table diff. `null` when neither side has line items. */
  lines: LinesDiff | null;
  /**
   * Named tables for every other array-of-objects we encountered (e.g. `allowanceCharges`).
   * Each gets the same column/row treatment as `lines` so the UI can render them consistently
   * instead of falling back to inline JSON.
   */
  objectArrays: ObjectArrayDiff[];
  /** Total count of changed fields, including line items and each changed object-array as a unit. */
  changedCount: number;
  lineCount: number;
  lineCountWas?: number;
}

/** Parse the upstream `resultInlineJson` blob, swallowing parse errors. */
export function parsePreviewResult(json: string | undefined | null): unknown {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Build a structured diff over every field in the baseline vs proposed payloads. Walks both
 * trees generically — no hardcoded field catalogue — so new fields upstream surface
 * automatically. Line items get a side-by-side table when count differs or any row differs.
 *
 * Tolerates the wire shape variations we've seen:
 *   - Proposed may be flat or wrapped under `document` / `documentAIResult`.
 *   - Keys may be camelCase or PascalCase — we normalize on read.
 */
export function previewSummary(
  proposed: unknown,
  baseline: DocumentExtraction | null
): PreviewSummary {
  const empty: PreviewSummary = {
    scalarRows: [],
    scalarChanges: [],
    lines: null,
    objectArrays: [],
    changedCount: 0,
    lineCount: 0
  };

  const proposedDoc = unwrapDocument(proposed);
  const baselineDoc = unwrapDocument(baseline?.document ?? null);

  if (proposedDoc === null && baselineDoc === null) return empty;

  const scalarRows: SummaryRow[] = [];
  const objectArrays: ObjectArrayDiff[] = [];
  const linesField = findLinesKey(proposedDoc, baselineDoc);

  walkScalars('', proposedDoc, baselineDoc, linesField, scalarRows, objectArrays);
  scalarRows.sort((a, b) => a.label.localeCompare(b.label));
  objectArrays.sort((a, b) => a.label.localeCompare(b.label));

  const scalarChanges = scalarRows.filter((row) => row.was !== undefined);

  const lines = linesField
    ? diffLines(
        readArray(proposedDoc, linesField),
        readArray(baselineDoc, linesField)
      )
    : null;

  const lineCount = lines?.after.filter((r) => !r.ghost).length ?? 0;
  const lineCountWas = lines?.before.filter((r) => !r.ghost).length ?? 0;

  let changedCount = scalarChanges.length;
  if (lines?.changed) changedCount += 1;
  changedCount += objectArrays.filter((arr) => arr.changed).length;

  return {
    scalarRows,
    scalarChanges,
    lines,
    objectArrays,
    changedCount,
    lineCount,
    lineCountWas: lines && lineCount !== lineCountWas ? lineCountWas : undefined
  };
}

// ---------- Walkers ----------

function walkScalars(
  prefix: string,
  proposed: unknown,
  baseline: unknown,
  linesKey: string | null,
  out: SummaryRow[],
  outArrays: ObjectArrayDiff[]
): void {
  const keys = unionKeys(proposed, baseline);
  for (const key of keys) {
    // Skip the line-items field — it's surfaced via the separate LinesDiff structure so users
    // see a table, not a flattened `documentLines[2].amount` row in the scalar list.
    if (prefix === '' && key === linesKey) continue;

    const path = prefix === '' ? key : `${prefix}.${key}`;
    const after = pickCaseInsensitive(proposed, key);
    const before = pickCaseInsensitive(baseline, key);

    if (isPlainObject(after) || isPlainObject(before)) {
      walkScalars(path, after, before, linesKey, out, outArrays);
      continue;
    }

    if (Array.isArray(after) || Array.isArray(before)) {
      // Arrays of `{ fieldName, fieldValue }` (e.g. customFields) are really a flat key/value
      // bag in disguise. Flatten so each entry surfaces as its own row — "Custom fields › X"
      // — instead of being dumped as inline JSON.
      const afterFlat = flattenKeyValueArray(after);
      const beforeFlat = flattenKeyValueArray(before);
      if (afterFlat || beforeFlat) {
        walkScalars(path, afterFlat ?? {}, beforeFlat ?? {}, linesKey, out, outArrays);
        continue;
      }
      // Arrays of plain objects (e.g. allowanceCharges) get the same table treatment as line
      // items so users see a structured before/after rather than a `JSON.stringify` dump.
      if (isArrayOfPlainObjects(after) || isArrayOfPlainObjects(before)) {
        const afterArr = Array.isArray(after) ? after : [];
        const beforeArr = Array.isArray(before) ? before : [];
        const diff = diffLines(afterArr, beforeArr);
        outArrays.push({
          field: path,
          label: humanizePath(path),
          rowCount: diff.after.filter((r) => !r.ghost).length,
          rowCountWas: diff.before.filter((r) => !r.ghost).length,
          ...diff
        });
        continue;
      }
      const afterStr = formatArrayInline(after);
      const beforeStr = formatArrayInline(before);
      if (valueEquals(after, before)) {
        out.push({ field: path, label: humanizePath(path), value: afterStr });
      } else {
        out.push({ field: path, label: humanizePath(path), value: afterStr, was: beforeStr });
      }
      continue;
    }

    const afterStr = formatScalar(after);
    const beforeStr = formatScalar(before);
    if (valueEquals(after, before)) {
      out.push({ field: path, label: humanizePath(path), value: afterStr });
    } else {
      out.push({ field: path, label: humanizePath(path), value: afterStr, was: beforeStr });
    }
  }
}

/** Arrays shaped like `[{ fieldName, fieldValue }, …]` collapse to a `{ name: value }` record. */
function flattenKeyValueArray(value: unknown): Record<string, unknown> | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const out: Record<string, unknown> = {};
  for (const item of value) {
    if (!isPlainObject(item)) return null;
    const name = pickCaseInsensitive(item, 'fieldName');
    if (typeof name !== 'string' || name === '') return null;
    const obj = item as Record<string, unknown>;
    if (!('fieldValue' in obj) && !('FieldValue' in obj)) return null;
    out[name] = pickCaseInsensitive(item, 'fieldValue');
  }
  return out;
}

function diffLines(afterLines: unknown[], beforeLines: unknown[]): LinesDiff {
  const colsOrder: string[] = [];
  const colsSet = new Set<string>();
  const addCols = (lines: unknown[]) => {
    for (const line of lines) {
      if (!isPlainObject(line)) continue;
      for (const k of Object.keys(line as Record<string, unknown>)) {
        const key = lowerFirst(k);
        if (!colsSet.has(key)) {
          colsSet.add(key);
          colsOrder.push(key);
        }
      }
    }
  };
  addCols(afterLines);
  addCols(beforeLines);

  // Drop columns whose values are empty on every line in both sides — they have nothing to
  // show (e.g. per-line `customFields: []`) and previously surfaced as ugly `[]` / `{}` cells
  // that also got flagged as "changed" because each empty array was a distinct reference.
  const columns = colsOrder
    .filter((field) => columnHasContent(field, afterLines, beforeLines))
    .map((field) => ({ field, label: humanizeKey(field) }));

  const max = Math.max(afterLines.length, beforeLines.length);
  const before: LineRow[] = [];
  const after: LineRow[] = [];
  let anyChange = afterLines.length !== beforeLines.length;

  for (let i = 0; i < max; i += 1) {
    const a = afterLines[i];
    const b = beforeLines[i];
    const aGhost = i >= afterLines.length;
    const bGhost = i >= beforeLines.length;

    const cellsA: LineCell[] = [];
    const cellsB: LineCell[] = [];
    for (const col of columns) {
      const av = pickCaseInsensitive(a, col.field);
      const bv = pickCaseInsensitive(b, col.field);
      const changed = !aGhost && !bGhost && !valueEquals(av, bv);
      if (changed) anyChange = true;
      cellsA.push({
        field: col.field,
        label: col.label,
        value: formatScalar(av),
        was: changed ? formatScalar(bv) : undefined
      });
      cellsB.push({
        field: col.field,
        label: col.label,
        value: formatScalar(bv),
        was: changed ? formatScalar(av) : undefined
      });
    }
    after.push({ index: i, cells: cellsA, ghost: aGhost });
    before.push({ index: i, cells: cellsB, ghost: bGhost });
  }

  return { columns, before, after, changed: anyChange };
}

// ---------- Shape helpers ----------

function unwrapDocument(value: unknown): unknown {
  if (!isPlainObject(value)) return value ?? null;
  // Proposed payloads sometimes arrive wrapped under `document` / `documentAIResult`; the
  // baseline from the BFF is the inner shape already, but we accept either.
  for (const wrap of ['document', 'documentAIResult', 'Document', 'DocumentAIResult']) {
    if (wrap in (value as Record<string, unknown>)) {
      const inner = (value as Record<string, unknown>)[wrap];
      if (isPlainObject(inner)) return inner;
    }
  }
  return value;
}

function findLinesKey(a: unknown, b: unknown): string | null {
  const candidates = ['documentLines', 'lines', 'lineItems'];
  for (const candidate of candidates) {
    if (Array.isArray(pickCaseInsensitive(a, candidate))) return candidate;
    if (Array.isArray(pickCaseInsensitive(b, candidate))) return candidate;
  }
  return null;
}

function readArray(value: unknown, key: string): unknown[] {
  const v = pickCaseInsensitive(value, key);
  return Array.isArray(v) ? v : [];
}

function unionKeys(a: unknown, b: unknown): string[] {
  const set = new Set<string>();
  const push = (v: unknown) => {
    if (!isPlainObject(v)) return;
    for (const k of Object.keys(v as Record<string, unknown>)) {
      set.add(lowerFirst(k));
    }
  };
  push(a);
  push(b);
  return [...set];
}

function pickCaseInsensitive(obj: unknown, key: string): unknown {
  if (!isPlainObject(obj)) return undefined;
  const o = obj as Record<string, unknown>;
  if (key in o) return o[key];
  const pascal = upperFirst(key);
  if (pascal in o) return o[pascal];
  // Fallback: case-insensitive linear scan for keys where the case mismatch is deeper than
  // just the first character (e.g., `vatID` vs `VatId`). Keeps the common path (camelCase
  // hit) fast.
  const lower = key.toLowerCase();
  for (const k of Object.keys(o)) {
    if (k.toLowerCase() === lower) return o[k];
  }
  return undefined;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isArrayOfPlainObjects(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.length > 0 && value.every(isPlainObject);
}

// ---------- Formatters ----------

function normalize(value: unknown): unknown {
  if (value === undefined || value === '') return null;
  return value;
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value as Record<string, unknown>).length === 0;
  return false;
}

function columnHasContent(field: string, ...sides: unknown[][]): boolean {
  for (const rows of sides) {
    for (const row of rows) {
      if (!isEmptyValue(pickCaseInsensitive(row, field))) return true;
    }
  }
  return false;
}

function valueEquals(a: unknown, b: unknown): boolean {
  const na = normalize(a);
  const nb = normalize(b);
  if (na === nb) return true;
  if (na === null || nb === null) return na === nb;
  if (Array.isArray(na) && Array.isArray(nb)) {
    if (na.length !== nb.length) return false;
    for (let i = 0; i < na.length; i += 1) {
      if (!valueEquals(na[i], nb[i])) return false;
    }
    return true;
  }
  if (isPlainObject(na) && isPlainObject(nb)) {
    const oa = na as Record<string, unknown>;
    const ob = nb as Record<string, unknown>;
    const keys = new Set([...Object.keys(oa), ...Object.keys(ob)]);
    for (const k of keys) {
      if (!valueEquals(oa[k], ob[k])) return false;
    }
    return true;
  }
  return false;
}

function formatScalar(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') {
    return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatArrayInline(value: unknown): string {
  if (!Array.isArray(value) || value.length === 0) return '—';
  return value.map(formatScalar).join(', ');
}

function humanizePath(path: string): string {
  return path.split('.').map(humanizeKey).join(' › ');
}

function humanizeKey(key: string): string {
  // Split camelCase / PascalCase / snake_case into words and Title Case the result.
  const withSpaces = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
  if (!withSpaces) return key;
  return withSpaces
    .split(' ')
    .map((w, i) => (i === 0 ? upperFirst(w) : w.toLowerCase()))
    .join(' ');
}

function lowerFirst(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toLowerCase() + s.slice(1);
}

function upperFirst(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}
